import { useEffect, useRef } from 'react';

import { useSearchParams } from 'react-router';

import { CustomerRow, TabPath } from '../CustomersV2.types';
import { canOpenProfile } from '../profileEligibility';
import { trackCustomerProfileOpened } from '../tracking';

export function useProfileBuyer({
  rows,
  loading,
  tab,
}: {
  rows: CustomerRow[];
  loading: boolean;
  /** Only needed to attribute a `?buyer=` open to a tab; omit to skip tracking. */
  tab?: TabPath;
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const buyerStateKey = searchParams.get('buyer');
  const matchedRow =
    rows.find((row) => row.buyer_state_key === buyerStateKey) ?? null;
  const selectedRow =
    matchedRow && canOpenProfile(matchedRow) ? matchedRow : null;

  // A shared or stale link can name a buyer that isn't on the current page, or
  // one with no profile to show. Drop the param once rows have settled so the
  // URL never claims a profile that isn't open. Guarded on `loading` so a valid
  // key survives the fetch.
  const isStale = Boolean(buyerStateKey) && !loading && !selectedRow;
  useEffect(() => {
    if (!isStale) return;
    setSearchParams(
      (params) => {
        params.delete('buyer');
        return params;
      },
      { replace: true },
    );
  }, [isStale, setSearchParams]);

  // An in-app open writes `?buyer=` too, so the effect below would see it and
  // count it a second time as a link open. Keys opened from a control are
  // recorded here and skipped — the caller has already reported the real source.
  const openedInAppRef = useRef<string | null>(null);
  // The last key reported, so a re-render on any other param change doesn't
  // re-report the profile that is already open.
  const reportedRef = useRef<string | null>(null);

  useEffect(() => {
    if (tab === undefined) return;
    if (selectedRow === null) {
      // Closing clears the latch so reopening the same buyer counts again.
      reportedRef.current = null;
      return;
    }
    if (selectedRow.buyer_state_key === reportedRef.current) return;

    reportedRef.current = selectedRow.buyer_state_key;
    if (openedInAppRef.current === selectedRow.buyer_state_key) {
      openedInAppRef.current = null;
      return;
    }
    trackCustomerProfileOpened({ tab, source: 'url', row: selectedRow });
  }, [selectedRow, tab]);

  const openProfile = (nextBuyerStateKey: string) => {
    openedInAppRef.current = nextBuyerStateKey;
    setSearchParams((params) => {
      params.set('buyer', nextBuyerStateKey);
      return params;
    });
  };

  const closeProfile = () => {
    setSearchParams((params) => {
      params.delete('buyer');
      return params;
    });
  };

  return { selectedRow, openProfile, closeProfile };
}
